import { type ResponseData, useApiQuery, useTeam } from '@vrplatform/data';
import { type Maybe, day, isUUID, utc } from '@vrplatform/utils';
import type { listing as Listing } from '@vrplatform/graphql';
import { whereGlListingIsInactive } from '@vrplatform/graphql';
import { formatAddress } from '@vrplatform/ui-common';

type ListingDetailApiResponse = ResponseData<'get:/listings/{id}'>;

const getListingDetailAddress = (
  address: ListingDetailApiResponse['address']
) => {
  if (!address) {
    return { full: undefined };
  }

  const full =
    address.full ||
    formatAddress({
      line1: address.line1 || '',
      line2: address.line2 || '',
      city: address.city || '',
      state: address.state || '',
      stateCode: address.stateCode || '',
      country: '',
      countryCode: address.countryCode || '',
      postcode: address.postalCode || '',
    }) ||
    undefined;

  return {
    ...address,
    full,
  };
};

const getListingDeactivationFromApi = (
  upcoming: ListingDetailApiResponse['upcomingDeactivation']
) => {
  if (!upcoming || upcoming.status === 'active') {
    return {
      status: 'active',
      period: null,
    } as const;
  }

  if (!upcoming.period) {
    return {
      status: 'active',
      period: null,
    } as const;
  }

  const period = {
    id: upcoming.period.id,
    startAt: utc(upcoming.period.startAt).yyyymmdd(),
    endAt: upcoming.period.endAt ? utc(upcoming.period.endAt).yyyymmdd() : null,
  };

  if (upcoming.status === 'disable-upcoming') {
    return {
      status: 'upcoming',
      period,
    } as const;
  }

  if (upcoming.status === 'disabled-ending') {
    return {
      status: 'disabled-ending',
      period,
    } as const;
  }

  if (upcoming.status === 'disabled-forever') {
    return {
      status: 'disabled',
      period: {
        ...period,
        endAt: null,
      },
    } as const;
  }

  return {
    status: 'active',
    period: null,
  } as const;
};

const getListingCollectionFromApi = (listing: ListingDetailApiResponse) => {
  const listingWithCollection = listing as ListingDetailApiResponse & {
    collection?: { id?: string | null; name?: string | null } | null;
    collectionId?: string | null;
  };

  if (listingWithCollection.collection || listingWithCollection.collectionId) {
    return {
      id:
        listingWithCollection.collection?.id ??
        listingWithCollection.collectionId ??
        null,
      name: listingWithCollection.collection?.name || '',
    };
  }

  return null;
};

export type ListingData = NonNullable<
  NonNullable<ReturnType<typeof useListingDetailViewQuery>['data']>['listing']
>;

export function useListingDetailViewQuery(listingId: Maybe<string>) {
  const [{ id: teamId }] = useTeam();

  return useApiQuery(
    async (api, signal, args) => {
      if (!args.listingId)
        return {
          listing: null,
        };

      const result = await api.GET('/listings/{id}', {
        params: {
          path: {
            id: args.listingId,
          },
        },
        signal,
      });

      if (result.error) {
        if (result.error.code === 'NOT_FOUND')
          return {
            listing: null,
          };
        throw result.error;
      }

      if (!result.data)
        return {
          listing: null,
        };

      const listing = result.data;

      return {
        listing: {
          id: listing.id,
          name: listing.name,
          address: getListingDetailAddress(listing.address),
          deactivation: getListingDeactivationFromApi(
            listing.upcomingDeactivation
          ),
          activeOwnership: listing.activeOwnership,
          activeRecurringFeePeriods: listing.activeRecurringFeePeriods,
          sourceStatus: listing.source?.status ?? undefined,
          pmsStatus: listing.source?.status ?? undefined,
          collection: getListingCollectionFromApi(listing),
          tenantId: teamId,
        },
      };
    },
    {
      keepPreviousData: true,
      queryGroup: 'listings',
      uniqueQueryKey: 'listing-detail-view',
      variables: { listingId },
      skip: !listingId || !isUUID(listingId),
    }
  );
}

export const getListingDeactivation = (listing: Listing, today: string) => {
  const disabledPeriod = listing
    .ownershipPeriods({
      limit: 1,
      order_by: [
        {
          startAt: 'asc_nulls_last',
        },
      ],
      where: whereGlListingIsInactive(undefined, today, true).ownershipPeriods!,
    })
    .map((p) => ({
      id: p.id,
      startAt: utc(p.startAt!).yyyymmdd(),
      endAt: p.endAt ? utc(p.endAt).yyyymmdd() : null,
    }))[0];

  if (!disabledPeriod) {
    return {
      status: 'active',
      period: null,
    } as const;
  }

  if (day(today).isBefore(disabledPeriod.startAt)) {
    return {
      status: 'upcoming',
      period: disabledPeriod,
    } as const;
  }

  if (disabledPeriod.endAt) {
    return {
      status: 'disabled-ending',
      period: {
        ...disabledPeriod,
        endAt: disabledPeriod.endAt,
      },
    } as const;
  }

  return {
    status: 'disabled',
    period: {
      ...disabledPeriod,
      endAt: null,
    },
  } as const;
};
